import { FunctionsHttpError } from "@supabase/supabase-js";
import { AppError, toAppError } from "../../lib/appError";
import type { Json } from "../../lib/database.types";
import { supabase } from "../../lib/supabaseClient";
import { agentRepository, type ConversationHandlers } from "./agentRepository";
import {
  appliedChangesOf,
  toChatMessage,
  type AgentChatMessage,
  type AgentConversation,
  type AgentTurn,
  type AppliedChange,
} from "./agentTranscript";

export type { AgentChatMessage };

// HAZ-48 decision: conversations are kept for one week, timed on the last
// interaction — the sweep-agent-conversations schedule deletes what is
// older. The panel shows the same number so a returning user understands
// why their history vanished.
export const CONVERSATION_RETENTION_LABEL = "Conversation history is kept for 7 days.";

export const WorkingNotice = "Hazumu is working on it…";

// The runtime lives at supabase/functions/ai-agent. While the function is
// not deployed (or `DUCKY_ENABLED` is false in tests) the panel surfaces
// this notice; the message is still persisted so history is continuous
// from the first session onwards.
export const AGENT_NOT_READY =
  "Ducky isn't connected yet — your message is saved and will be answered once it goes live.";

export type RunTurnInput = {
  conversationId: string;
  message: string;
  // The id of the user row the frontend already persisted (HAZ-48:
  // transcripts are continuous from the first session onwards, so the
  // browser owns the user insert even when the runtime is available).
  // The runtime looks the row up and skips its own insert when present,
  // so the same text never lands twice in the transcript.
  userMessageId?: string;
};

// The function answers 202 as soon as the turn exists; everything the turn
// does arrives as rows (Realtime), so the only thing to hand back is its id.
export type RunTurnResult = {
  turnId: string;
};

// Code on the AppError `runTurn` throws when the conversation already has a
// running turn (HTTP 409 from the function).
export const TURN_IN_PROGRESS = "TURN_IN_PROGRESS";

type FunctionErrorBody = { error?: string; code?: string };

// Reads the function's JSON error body once (a Response body can't be read
// twice) along with the HTTP status.
const readFunctionError = async (
  error: unknown,
): Promise<{ status: number | null; body: FunctionErrorBody | null }> => {
  if (!(error instanceof FunctionsHttpError)) {
    return { status: null, body: null };
  }
  const body = (await error.context.json().catch(() => null)) as
    | FunctionErrorBody
    | null;
  return { status: error.context.status, body };
};

export const agentService = {
  // The active conversation: the site's newest one (the retention sweep
  // keeps the table short), created on demand. This keeps the UI at "one
  // conversation per site" without a constraint, so supporting many
  // conversations later is a service change, not a schema one.
  async activeConversation(siteId: string, userId: string) {
    const conversations = await agentRepository.listConversations(siteId);
    return conversations[0] ?? (await agentRepository.createConversation(siteId, userId));
  },

  // Clears the surface by moving to a fresh conversation rather than
  // deleting rows: history is append-only (HAZ-48), so the old conversation
  // stays until the retention sweep. `activeConversation` picks the newest
  // row, so the new one becomes active for every subsequent load.
  startConversation: (siteId: string, userId: string) =>
    agentRepository.createConversation(siteId, userId),

  listMessages: (conversationId: string) =>
    agentRepository.listMessages(conversationId),

  appendUserMessage: (conversationId: string, content: string) =>
    agentRepository.createMessage(conversationId, { role: "user", content: content.trim() }),

  appendAssistantMessage: (conversationId: string, content: string, toolCalls?: Json) =>
    agentRepository.createMessage(conversationId, {
      role: "assistant",
      content,
      ...(toolCalls === undefined ? {} : { tool_calls: toolCalls }),
    }),

  appendToolMessage: (conversationId: string, toolCalls: Json) =>
    agentRepository.createMessage(conversationId, {
      role: "tool",
      content: "",
      tool_calls: toolCalls,
    }),

  // HAZ-49: start a turn. The function answers 202 `{ turn_id }` right away
  // and works in the background; the transcript and the turn's status then
  // arrive through `subscribeToConversation`. A conversation that already has
  // a running turn answers 409, thrown as an AppError coded TURN_IN_PROGRESS.
  async runTurn(input: RunTurnInput): Promise<RunTurnResult> {
    try {
      const { data, error } = await supabase.functions.invoke<{
        turn_id?: string;
      }>("ai-agent", {
        body: {
          conversation_id: input.conversationId,
          message: input.message.trim(),
          ...(input.userMessageId
            ? { user_message_id: input.userMessageId }
            : {}),
        },
      });
      if (error) {
        const { status, body } = await readFunctionError(error);
        if (status === 409 || body?.code === "turn_in_progress") {
          throw new AppError(
            body?.error ?? "Ducky is still working on your last message.",
            TURN_IN_PROGRESS,
            error,
          );
        }
        throw new AppError(
          body?.error ??
            (error instanceof Error
              ? error.message
              : "Ducky is not available right now."),
          "AGENT_TURN_FAILED",
          error,
        );
      }
      if (!data?.turn_id) {
        throw new AppError("Ducky returned an empty response.", "AGENT_EMPTY");
      }
      return { turnId: data.turn_id };
    } catch (error) {
      if (error instanceof AppError) throw error;
      throw toAppError(error as { message: string }, "Ducky is not available right now.");
    }
  },

  // The conversation's newest turn (null before the first message). Together
  // with the transcript this is everything the chat needs to resume mid-turn.
  latestTurn: (conversationId: string) =>
    agentRepository.latestTurn(conversationId),

  // Ask the running turn to stop; the turn row's status changes once the
  // worker notices (within one heartbeat).
  cancelTurn: (turnId: string) => agentRepository.cancelTurn(turnId),

  // Live message and turn rows for a conversation. Returns the unsubscribe.
  subscribeToConversation: (
    conversationId: string,
    handlers: ConversationHandlers,
  ) => agentRepository.subscribeToConversation(conversationId, handlers),

  toChatMessage,

  changesOf: appliedChangesOf,
};

export type { AgentConversation, AgentTurn, AppliedChange };
